// ABOUTME: Admin user detail view with comprehensive profile information and edit capabilities
// ABOUTME: Displays user details, family memberships, admin controls, edit forms, and soft delete functionality

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Save,
  Cancel,
  Email,
  AdminPanelSettings,
  CalendarToday,
  CheckCircle,
  Group,
  Restore,
  VpnKey,
  PersonOff
} from '@mui/icons-material';
import * as api from '../services/api';
import { formatRelativeTime, formatFriendlyDate } from '../utils/timeUtils';
import { getAvatarUrl } from '../utils/avatarUtils';
import { AdminPasswordResetModal } from './AdminPasswordResetModal';
import RoleEditModal from './RoleEditModal';
import { roleAPI } from '../services/api';
import { useImpersonation } from '../hooks/useImpersonation';
import { useAuth } from '../contexts/AuthContext';

interface UserDetail {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
  role?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  authProvider: string;
  emailVerificationStatus: string;
  isAdmin: boolean;
  lastActiveAt?: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  families: FamilyMembership[];
  totalFamilies: number;
  totalChildren: number;
}

interface FamilyMembership {
  id: number;
  name: string;
  role: string;
  joinedAt: string;
  childrenCount: number;
}

interface EditUserData {
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl: string;
  mantra: string;
  familyLoveQuestion: string;
  familyNeedQuestion: string;
  isAdmin: boolean;
}

const EMPTY_EDIT_DATA: EditUserData = {
  firstName: '',
  lastName: '',
  email: '',
  profileImageUrl: '',
  mantra: '',
  familyLoveQuestion: '',
  familyNeedQuestion: '',
  isAdmin: false,
};

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditUserData>(EMPTY_EDIT_DATA);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [roleEditModalOpen, setRoleEditModalOpen] = useState(false);
  const [editingUserRole, setEditingUserRole] = useState<{userId: number, currentRole: string, familyId: number} | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUserDetail(Number(userId));
    }
  }, [userId]);

  const fetchUserDetail = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const userData = await api.getUserDetail(id) as UserDetail;
      setUser(userData);
      setEditData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        profileImageUrl: userData.profileImageUrl || '',
        mantra: userData.mantra || '',
        familyLoveQuestion: userData.familyLoveQuestion || '',
        familyNeedQuestion: userData.familyNeedQuestion || '',
        isAdmin: userData.isAdmin
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('not found')) {
        setError('User not found');
      } else {
        setError('Failed to load user details. Please try again.');
      }
      console.error('Error fetching user detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (user) {
      setEditData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        profileImageUrl: user.profileImageUrl || '',
        mantra: user.mantra || '',
        familyLoveQuestion: user.familyLoveQuestion || '',
        familyNeedQuestion: user.familyNeedQuestion || '',
        isAdmin: user.isAdmin
      });
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await api.updateUser(user.id, editData);
      setUser({ ...user, ...editData });
      setIsEditing(false);
      setSuccessMessage('User updated successfully');
    } catch (err: unknown) {
      const responseMessage = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      const message = err instanceof Error ? err.message : String(err);
      setError(responseMessage || message || 'Error updating user. Please try again.');
      console.error('Error updating user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    try {
      setDeleting(true);
      await api.deleteUser(user.id);
      setSuccessMessage('User deleted successfully');
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (err: unknown) {
      const errorMessage = (axios.isAxiosError(err) ? err.response?.data?.message : undefined) || 'Error deleting user. Please try again.';
      setError(errorMessage);
      console.error('Error deleting user:', err);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handlePasswordResetSuccess = (message: string) => {
    setSuccessMessage(message);
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleSkipEmailVerification = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await api.default.put(`/api/admin/users/${user.id}/skip-verification`);
      setSuccessMessage('Email verification skipped successfully');
      // Refresh user data
      await fetchUserDetail(user.id);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Error skipping email verification. Please try again.');
      console.error('Error skipping email verification:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    // Coming soon functionality - placeholder for restore
  };

  const handleBackToList = () => {
    navigate('/admin/users');
  };

  const handleImpersonate = async () => {
    if (!user) return;

    try {
      await startImpersonation(user.id);

      // Use full page reload to ensure Banner and all components see the new state
      // Client-side navigation leaves Banner with stale state from initial mount
      window.location.replace('/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message || 'Failed to start impersonation');
      console.error('Error starting impersonation:', err);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress data-testid="loading-spinner" />
        </Box>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }} data-testid="user-detail-header">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ mr: 2 }}
        >
          Back to User List
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            {user.firstName} {user.lastName}
            {user.isDeleted && (
              <Chip label="DELETED" color="error" sx={{ ml: 2 }} />
            )}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            User ID: {user.id} • Account Created: {formatFriendlyDate(user.createdAt)}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {user.isDeleted ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Restore />}
              onClick={handleRestore}
              title="Restore user (Coming Soon)"
            >
              Restore User
            </Button>
          ) : (
            <>
              {isEditing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleEdit}
                  >
                    Edit User
                  </Button>
                  {user.authProvider === 'Email' && (
                    <Button
                      variant="outlined"
                      startIcon={<VpnKey />}
                      onClick={() => setPasswordResetModalOpen(true)}
                    >
                      Reset Password
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete User
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Impersonation Section - Distinct from edit/delete actions */}
      {!user.isDeleted && (
        <Paper sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Impersonate User
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.userId === user.id
                  ? 'You cannot impersonate yourself'
                  : 'View the application as this user sees it for customer support purposes'
                }
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              startIcon={<PersonOff />}
              onClick={handleImpersonate}
              disabled={currentUser?.userId === user.id}
              data-testid="impersonate-user-button"
            >
              Impersonate User
            </Button>
          </Box>
        </Paper>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Profile Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Profile Information
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Avatar
                    src={getAvatarUrl(user.profileImageUrl)}
                    alt={`${user.firstName} ${user.lastName} profile`}
                    sx={{ width: 120, height: 120, mb: 2 }}
                  />
                  {user.isAdmin && (
                    <Chip
                      label="Admin"
                      color="primary"
                      icon={<AdminPanelSettings />}
                    />
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 9 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="First Name"
                      value={isEditing ? editData.firstName : user.firstName}
                      onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                      disabled={!isEditing}
                      fullWidth
                      data-testid="edit-firstName"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Last Name"
                      value={isEditing ? editData.lastName : user.lastName}
                      onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                      disabled={!isEditing}
                      fullWidth
                      data-testid="edit-lastName"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Email"
                      value={isEditing ? editData.email : user.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      disabled={!isEditing}
                      fullWidth
                      data-testid="edit-email"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Mantra"
                      value={isEditing ? editData.mantra : user.mantra || 'Not set'}
                      onChange={(e) => setEditData({ ...editData, mantra: e.target.value })}
                      disabled={!isEditing}
                      fullWidth
                      multiline
                      rows={2}
                      data-testid="edit-mantra"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isEditing ? editData.isAdmin : user.isAdmin}
                          onChange={(e) => setEditData({ ...editData, isAdmin: e.target.checked })}
                          disabled={!isEditing}
                          data-testid="admin-toggle"
                        />
                      }
                      label="Admin User"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {/* Questions */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
              Profile Questions
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="How My Family Shows Love"
                  value={isEditing ? editData.familyLoveQuestion : user.familyLoveQuestion || 'Not answered'}
                  onChange={(e) => setEditData({ ...editData, familyLoveQuestion: e.target.value })}
                  disabled={!isEditing}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="What My Family Needs From Me"
                  value={isEditing ? editData.familyNeedQuestion : user.familyNeedQuestion || 'Not answered'}
                  onChange={(e) => setEditData({ ...editData, familyNeedQuestion: e.target.value })}
                  disabled={!isEditing}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Side Panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Account Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
              Account Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary="Auth Provider"
                  secondary={user.authProvider}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color={user.emailVerificationStatus === 'Verified' ? 'success' : 'disabled'} />
                </ListItemIcon>
                <ListItemText
                  primary="Email Verification Status"
                  secondary={user.emailVerificationStatus === 'Verified' ? 'Verified' :
                             user.emailVerificationStatus === 'VerificationSkipped' ? 'Verification Skipped' :
                             'Not Verified'}
                />
              </ListItem>
              {user.emailVerificationStatus === 'NotVerified' && !user.isDeleted && (
                <ListItem>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={handleSkipEmailVerification}
                    disabled={saving}
                    data-testid="skip-verification-button"
                  >
                    Skip Email Verification
                  </Button>
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <CalendarToday />
                </ListItemIcon>
                <ListItemText
                  primary="Last Active"
                  secondary={formatRelativeTime(user.lastActiveAt)}
                />
              </ListItem>
            </List>
          </Paper>

          {/* Family Memberships */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
              Family Memberships
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Families: {user.totalFamilies}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Children: {user.totalChildren}
              </Typography>
            </Box>

            
            {user.families?.length > 0 ? (
              <List dense>
                {user.families.map((family) => (
                  <ListItem key={family.id} sx={{ pl: 0 }}>
                    <ListItemIcon>
                      <Group />
                    </ListItemIcon>
                    <ListItemText
                      primary={family.name}
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" display="block">
                              Role: {user.role || 'Not set'}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUserRole({
                                  userId: user.id,
                                  currentRole: user.role || '',
                                  familyId: family.id
                                });
                                setRoleEditModalOpen(true);
                              }}
                              sx={{ 
                                fontSize: '0.7rem', 
                                py: 0.25, 
                                px: 1,
                                minWidth: '60px'
                              }}
                              data-testid="family-role-edit-button"
                            >
                              Edit
                            </Button>
                          </Box>
                          <Typography variant="caption" display="block">
                            Joined: {formatFriendlyDate(family.joinedAt)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Children: {family.childrenCount}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No family memberships
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Modal */}
      {user && (
        <AdminPasswordResetModal
          open={passwordResetModalOpen}
          user={{
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
          }}
          onClose={() => setPasswordResetModalOpen(false)}
          onSuccess={handlePasswordResetSuccess}
        />
      )}

      {/* Role Edit Modal */}
      {editingUserRole && (
        <RoleEditModal
          open={roleEditModalOpen}
          onClose={() => {
            setRoleEditModalOpen(false);
            setEditingUserRole(null);
          }}
          onSave={async (newRole: string) => {
            if (editingUserRole) {
              try {
                await roleAPI.adminUpdateUserRole(editingUserRole.userId, editingUserRole.familyId, newRole);
                // Refresh user data
                await fetchUserDetail(user?.id || editingUserRole.userId);
                setSuccessMessage('Role updated successfully');
              } catch (err) {
                console.error('Error updating role:', err);
                setError('Failed to update role');
              }
            }
          }}
          currentRole={editingUserRole.currentRole}
          userId={editingUserRole.userId}
          familyId={editingUserRole.familyId}
          isAdmin={true}
        />
      )}
    </Container>
  );
};

export default AdminUserDetail;